import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, Input, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { finalize, startWith } from 'rxjs';
import { PAYMENT_METHODS, PaymentMethod } from '@core/types/cash';
import { maxDecimalsValidator } from '@core/utils/form.validators';
import { formatCurrency } from '@core/utils/number.utils';
import {
  ICashSession,
  ICashSessionPaymentDetail,
  ICashSessionPaymentMethodCounts,
  ICashSessionPaymentMethodRowView,
  ICashSessionPaymentMethodsSummary,
} from '@models/cashSession.interface';
import { ButtonComponent } from '@shared/components/form/button/button.component';
import { AlertService } from '@core/services/alert.service';
import { ModalService } from '@core/services/modal.service';
import { CashSessionService } from '@modules/cash/cash-session/cash-session.service';
import { CashSessionPaymentMethodsTableComponent } from '@modules/cash/cash-session/components/cash-session-payment-methods-table/cash-session-payment-methods-table.component';
import { CashSessionEventService } from '@core/services/cash-session-event.service';

type CloseCashSessionStep = 'count' | 'cash-withdrawal';

export type CloseCashSessionModalResult = {
  type: 'success' | 'warning' | 'error';
  message: string;
  session?: ICashSession | null;
};

export type CloseCashSessionModalData = {
  session?: ICashSession;
  onSaved?: (result: CloseCashSessionModalResult) => void;
};

function cashWithdrawalAmountValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const paymentMethods = control.parent?.get('payment_methods_counted');
    const cashCounted = Number(paymentMethods?.get('cash')?.value ?? 0);
    const cashWithdrawalAmount = control.value;

    if (cashWithdrawalAmount === null || cashWithdrawalAmount === undefined || cashWithdrawalAmount === '') {
      return null;
    }

    const normalizedCashWithdrawal = Number(cashWithdrawalAmount);
    if (!Number.isFinite(normalizedCashWithdrawal)) {
      return null;
    }

    if (normalizedCashWithdrawal > cashCounted) {
      return { cashWithdrawalExceedsCountedCash: true };
    }

    return null;
  };
}

@Component({
    selector: 'app-close-cash-session-modal',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ButtonComponent,
        CashSessionPaymentMethodsTableComponent,
    ],
    providers: [CashSessionService],
    templateUrl: './close-cash-session-modal.component.html',
    styleUrl: './close-cash-session-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CloseCashSessionModalComponent implements OnInit {
  private static readonly SUGGESTED_CASH_LEFT_AMOUNT = 300;

  @Input() data: CloseCashSessionModalData | null = null;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cashSessionService = inject(CashSessionService);
  private readonly cashSessionEventService = inject(CashSessionEventService);
  private readonly modalService = inject(ModalService);
  private readonly alertService = inject(AlertService);

  readonly formatCurrency = formatCurrency;
  readonly isSubmitting = signal(false);
  readonly isLoadingSummary = signal(false);
  readonly currentStep = signal<CloseCashSessionStep>('count');
  readonly cashWithdrawalAmountErrors = {
    maxDecimals: 'El efectivo a retirar debe tener como máximo 2 decimales.',
    cashWithdrawalExceedsCountedCash:
      'El efectivo a retirar no puede ser mayor al efectivo contado.',
  };
  readonly paymentMethodsSummary = signal<ICashSessionPaymentMethodsSummary | null>(
    null,
  );

  readonly cashSessionForm = new FormGroup(
    {
      payment_methods_counted: new FormGroup({
        cash: new FormControl<number | null>(null, [
          Validators.required,
          Validators.min(0),
          maxDecimalsValidator(2),
        ]),
        debit_card: new FormControl<number | null>(null, [
          Validators.required,
          Validators.min(0),
          maxDecimalsValidator(2),
        ]),
        credit_card: new FormControl<number | null>(null, [
          Validators.required,
          Validators.min(0),
          maxDecimalsValidator(2),
        ]),
        transfers: new FormControl<number | null>(null, [
          Validators.required,
          Validators.min(0),
          maxDecimalsValidator(2),
        ]),
        other: new FormControl<number | null>(null, [
          Validators.required,
          Validators.min(0),
          maxDecimalsValidator(2),
        ]),
      }),
      cash_withdrawal_amount: new FormControl<number | null>(null, [
        Validators.required,
        Validators.min(0),
        maxDecimalsValidator(2),
        cashWithdrawalAmountValidator(),
      ]),
      notes: new FormControl('', [Validators.maxLength(300)]),
    },
  );

  readonly countedControls = {
    cash: this.cashSessionForm.controls.payment_methods_counted.controls.cash,
    debit_card:
      this.cashSessionForm.controls.payment_methods_counted.controls.debit_card,
    credit_card:
      this.cashSessionForm.controls.payment_methods_counted.controls.credit_card,
    transfers:
      this.cashSessionForm.controls.payment_methods_counted.controls.transfers,
    other: this.cashSessionForm.controls.payment_methods_counted.controls.other,
  };

  private readonly countedValues = toSignal(
    this.cashSessionForm.controls.payment_methods_counted.valueChanges.pipe(
      startWith(this.cashSessionForm.controls.payment_methods_counted.getRawValue()),
    ),
    {
      initialValue: this.cashSessionForm.controls.payment_methods_counted.getRawValue(),
    },
  );
  private readonly cashWithdrawalAmountValue = toSignal(
    this.cashSessionForm.controls.cash_withdrawal_amount.valueChanges.pipe(
      startWith(this.cashSessionForm.controls.cash_withdrawal_amount.value),
    ),
    {
      initialValue: this.cashSessionForm.controls.cash_withdrawal_amount.value,
    },
  );

  readonly previewRows = computed(() => {
    const baseRows = this.getCashSessionPaymentMethodRows(
      this.paymentMethodsSummary(),
    );
    const counted = this.countedValues();

    return baseRows.map((row) => {
      const countedAmount = Number(counted[row.bucket] ?? 0);
      return {
        ...row,
        counted_amount: countedAmount,
        difference_amount: Number(
          (countedAmount - Number(row.expected_amount ?? 0)).toFixed(2),
        ),
      };
    });
  });
  readonly countedCashAmount = computed(() => {
    const cashRow = this.previewRows().find((row) => row.bucket === 'cash');
    return Math.max(Number(cashRow?.counted_amount ?? 0), 0);
  });
  readonly suggestedCashLeftAmount = computed(
    () => CloseCashSessionModalComponent.SUGGESTED_CASH_LEFT_AMOUNT,
  );
  readonly suggestedCashWithdrawalAmount = computed(() =>
    Math.max(
      Number((this.countedCashAmount() - this.suggestedCashLeftAmount()).toFixed(2)),
      0,
    ),
  );
  readonly cashLeftAmount = computed(() => {
    const withdrawal = Number(this.cashWithdrawalAmountValue() ?? 0);

    return Math.max(
      Number((this.countedCashAmount() - withdrawal).toFixed(2)),
      0,
    );
  });

  ngOnInit(): void {
    this.cashSessionForm.controls.payment_methods_counted.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.cashSessionForm.controls.cash_withdrawal_amount.updateValueAndValidity({
          onlySelf: true,
          emitEvent: false,
        });
      });

    this.loadPaymentMethodsSummary();
  }

  get session(): ICashSession | null {
    return this.data?.session ?? null;
  }

  get countedInputsErrorMessage(): string {
    const countedGroup = this.cashSessionForm.controls.payment_methods_counted;

    if (!countedGroup.touched && !countedGroup.invalid) {
      return '';
    }

    if (countedGroup.invalid) {
      return 'Captura un monto válido para cada método de pago.';
    }

    return '';
  }

  get isCountStep(): boolean {
    return this.currentStep() === 'count';
  }

  get isCashWithdrawalStep(): boolean {
    return this.currentStep() === 'cash-withdrawal';
  }

  get showSuggestedShortcut(): boolean {
    const value = this.cashSessionForm.controls.cash_withdrawal_amount.value;
    return value === null || value === undefined;
  }

  confirmMoneyCount(): void {
    const countedGroup = this.cashSessionForm.controls.payment_methods_counted;

    if (countedGroup.invalid) {
      countedGroup.markAllAsTouched();
      return;
    }

    const cashWithdrawalControl = this.cashSessionForm.controls.cash_withdrawal_amount;
    if (cashWithdrawalControl.value === null || cashWithdrawalControl.value === undefined) {
      this.useSuggestedCashWithdrawalAmount(false);
    }
    this.currentStep.set('cash-withdrawal');
  }

  goBackToMoneyCount(): void {
    this.currentStep.set('count');
  }

  useSuggestedCashWithdrawalAmount(markAsTouched = true): void {
    const control = this.cashSessionForm.controls.cash_withdrawal_amount;
    control.setValue(this.suggestedCashWithdrawalAmount());

    if (markAsTouched) {
      control.markAsTouched();
    } else {
      control.markAsPristine();
      control.markAsUntouched();
    }

    control.updateValueAndValidity();
  }

  submit(): void {
    if (!this.session) {
      this.alertService.open({
        type: 'error',
        message: 'No se encontró la caja a cerrar.',
      });
      return;
    }

    if (this.session.status !== 'OPEN') {
      this.data?.onSaved?.({
        type: 'warning',
        message: 'La caja seleccionada ya no está abierta.',
      });
      this.close(true);
      return;
    }

    if (this.isSubmitting() || this.isLoadingSummary()) {
      return;
    }

    if (this.cashSessionForm.invalid) {
      this.cashSessionForm.markAllAsTouched();
      return;
    }

    if (!this.paymentMethodsSummary()) {
      this.alertService.open({
        type: 'error',
        message: 'No fue posible preparar la conciliación para este corte.',
      });
      return;
    }

    this.isSubmitting.set(true);
    const notes = String(this.cashSessionForm.controls.notes.value ?? '').trim() || undefined;

    this.cashSessionService
      .closeCashSession({
        cash_session_id: Number(this.session.id),
        payment_methods_counted: this.toPaymentMethodCounts(
          this.cashSessionForm.controls.payment_methods_counted.getRawValue(),
        ),
        cash_left_amount: this.cashLeftAmount(),
        notes,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isSubmitting.set(false);
        }),
      )
      .subscribe({
        next: (response) => {
          this.cashSessionEventService.notifyChanged();
          this.data?.onSaved?.({
            type: 'success',
            message: response.message || 'Caja cerrada exitosamente.',
            session: response.data ?? null,
          });
          this.close(true);
        },
        error: (error) => {
          const message =
            error?.error?.message || 'No fue posible cerrar la caja.';
          const errorCode = error?.error?.error?.code;

          if (
            errorCode === 'CASH_SESSION_ALREADY_CLOSED' ||
            errorCode === 'CASH_SESSION_NOT_OPEN'
          ) {
            this.data?.onSaved?.({
              type: 'warning',
              message,
            });
            this.close(true);
            return;
          }

          this.alertService.open({
            type: 'error',
            message,
          });
        },
      });
  }

  close(force = false): void {
    if (!force && (this.isSubmitting() || this.isLoadingSummary())) {
      return;
    }

    this.modalService.close();
  }

  private loadPaymentMethodsSummary(): void {
    if (!this.session?.id) {
      this.alertService.open({
        type: 'error',
        message: 'No se encontró la sesión que se intentaba cerrar.',
      });
      return;
    }

    this.isLoadingSummary.set(true);

    this.cashSessionService
      .getCashSessionPaymentMethodsById(Number(this.session.id))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoadingSummary.set(false);
        }),
      )
      .subscribe({
        next: (summary) => {
          if (!summary) {
            this.data?.onSaved?.({
              type: 'warning',
              message: 'La caja seleccionada ya no está disponible.',
            });
            this.close(true);
            return;
          }

          this.paymentMethodsSummary.set(summary);
          this.currentStep.set('count');
          this.cashSessionForm.controls.payment_methods_counted.patchValue(
            this.createInitialPaymentMethodCounts(summary),
            { emitEvent: true },
          );
          this.cashSessionForm.controls.cash_withdrawal_amount.setValue(null, {
            emitEvent: true,
          });
          this.cashSessionForm.controls.cash_withdrawal_amount.markAsPristine();
          this.cashSessionForm.controls.cash_withdrawal_amount.markAsUntouched();
          this.cashSessionForm.controls.notes.setValue('', {
            emitEvent: false,
          });
        },
        error: (error) => {
          this.alertService.open({
            type: 'error',
            message:
              error?.error?.message ||
              'No fue posible cargar la conciliación actual de la caja.',
          });
        },
      });
  }

  private createEmptyPaymentMethodCounts(): ICashSessionPaymentMethodCounts {
    return {
      cash: 0,
      debit_card: 0,
      credit_card: 0,
      transfers: 0,
      other: 0,
    };
  }

  private createInitialPaymentMethodCounts(
    summary: ICashSessionPaymentMethodsSummary,
  ): ICashSessionPaymentMethodCounts {
    const emptyCounts = this.createEmptyPaymentMethodCounts();

    return {
      ...emptyCounts,
      debit_card: Number(
        summary.payment_methods.debit_card.counted_amount ??
          summary.payment_methods.debit_card.expected_amount ??
          0,
      ),
      credit_card: Number(
        summary.payment_methods.credit_card.counted_amount ??
          summary.payment_methods.credit_card.expected_amount ??
          0,
      ),
    };
  }

  private getCashSessionPaymentMethodRows(
    source: ICashSessionPaymentMethodsSummary | ICashSession | null | undefined,
  ): ICashSessionPaymentMethodRowView[] {
    if (!source) {
      return [];
    }

    if ('payment_methods' in source && source.payment_methods) {
      return PAYMENT_METHODS.map((bucket) => {
        const metric = source.payment_methods[bucket];
        return {
          bucket,
          payment_method: bucket,
          label: this.getCashSessionPaymentMethodLabel(bucket),
          expected_amount: Number(metric?.expected_amount ?? 0),
          counted_amount:
            metric?.counted_amount === null || metric?.counted_amount === undefined
              ? null
              : Number(metric.counted_amount),
          difference_amount:
            metric?.difference_amount === null ||
            metric?.difference_amount === undefined
              ? null
              : Number(metric.difference_amount),
          sales_count: Number(metric?.sales_count ?? 0),
        };
      });
    }

    const sessionSource = source as ICashSession;
    const byMethod = new Map<PaymentMethod, ICashSessionPaymentDetail>();

    for (const detail of sessionSource.cashSessionPaymentDetailsObject ?? []) {
      byMethod.set(detail.payment_method, detail);
    }

    return PAYMENT_METHODS.map((bucket) => {
      const detail = byMethod.get(bucket);
      return {
        bucket,
        payment_method: bucket,
        label: this.getCashSessionPaymentMethodLabel(bucket),
        expected_amount: Number(detail?.expected_amount ?? 0),
        counted_amount:
          detail?.counted_amount === null || detail?.counted_amount === undefined
            ? null
            : Number(detail.counted_amount),
        difference_amount:
          detail?.difference_amount === null ||
          detail?.difference_amount === undefined
            ? null
            : Number(detail.difference_amount),
        sales_count: Number(detail?.sales_count ?? 0),
      };
    });
  }

  private getCashSessionPaymentMethodLabel(bucket: PaymentMethod): string {
    if (bucket === 'cash') return 'Efectivo';
    if (bucket === 'debit_card') return 'Tarjeta de débito';
    if (bucket === 'credit_card') return 'Tarjeta de crédito';
    if (bucket === 'transfers') return 'Transferencias';
    if (bucket === 'other') return 'Otros';
    return 'Método';
  }

  private toPaymentMethodCounts(
    values: Partial<Record<PaymentMethod, number | null | undefined>>,
  ): ICashSessionPaymentMethodCounts {
    const normalized = this.createEmptyPaymentMethodCounts();

    for (const bucket of PAYMENT_METHODS) {
      normalized[bucket] = Number(values[bucket] ?? 0);
    }

    return normalized;
  }

}
